import React from "react";
import SEOHead from "@/components/site/SEOHead";
import BlogLayout from "@/components/site/BlogLayout";

// --- SEO constants ----------------------------------------------------------
const CANONICAL = "https://emgroupdigital.fr/blog/website-cost-mauritius";
const CANONICAL_FR = "https://emgroupdigital.fr/blog/cout-site-web-maurice";
const TITLE = "How much does a website cost in Mauritius in 2026? — EM Group";
const DESCRIPTION =
  "Find out how much a professional website costs in Mauritius in 2026. Buy or rent from Rs 3,500/month — EM Group explains everything.";
const KEYWORDS =
  "website cost Mauritius, website price Mauritius 2026, cheap website Mauritius, website subscription Mauritius, professional website Mauritius, web agency Mauritius";
const OG_IMAGE = "https://emgroupdigital.fr/brand/logo-em-group.png";
const DATE_PUBLISHED = "2026-09-04";

const ARTICLE_SCHEMA = {
  headline: "How much does a website cost in Mauritius in 2026?",
  description: DESCRIPTION,
  image: OG_IMAGE,
  inLanguage: "en-MU",
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_PUBLISHED,
  canonical: CANONICAL,
  section: "Mauritius website guide",
  keywords: KEYWORDS,
};

const BREADCRUMB = [
  { name: "Home", item: "https://emgroupdigital.fr/" },
  { name: "Blog", item: "https://emgroupdigital.fr/blog" },
  { name: "Website cost in Mauritius", item: CANONICAL },
];

const ALTERNATES = [
  { hreflang: "en", href: CANONICAL },
  { hreflang: "en-MU", href: CANONICAL },
  { hreflang: "fr", href: CANONICAL_FR },
  { hreflang: "fr-MU", href: CANONICAL_FR },
  { hreflang: "x-default", href: CANONICAL_FR },
];

// --- Page -------------------------------------------------------------------
export default function BlogWebsiteCostMauritius() {
  return (
    <>
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        canonical={CANONICAL}
        ogImage={OG_IMAGE}
        ogType="article"
        locale="en_MU"
        htmlLang="en"
        robots="index, follow"
        articleSchema={ARTICLE_SCHEMA}
        breadcrumbSchema={BREADCRUMB}
        alternates={ALTERNATES}
      />

      <BlogLayout
        testId="blog-website-cost-mauritius"
        kicker="Guide · Mauritius"
        title="How much does a website cost in Mauritius in 2026?"
        lede="If you've ever asked for a website quote in Mauritius, you've probably received an invoice starting at Rs 25,000 — and it goes up quickly from there."
        datePublishedIso={DATE_PUBLISHED}
        dateLabel="September 4, 2026"
        readingMinutes={4}
        hreflangSwitcher={{ label: "Lire en français", to: "/blog/cout-site-web-maurice" }}
        backLabel="Back to home"
        cta={{
          eyebrow: "Free quote",
          title: "Want us to look at your project together?",
          subtitle:
            "We offer a free quote within 24 hours — rent, buy or full support, we'll find the formula that fits your budget and goals.",
          label: "Get my free quote",
          to: "/site-location-maurice",
        }}
      >
        <p>
          That's not necessarily unjustified. But what nobody tells you is that this model
          isn't the only one that exists. And more importantly, it may not be the most
          adapted to your situation.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Why does it cost so much?
        </h2>
        <p>
          A <strong className="text-white">professional website in Mauritius</strong>, when done properly, takes time. Design,
          development, content, images, mobile testing, going live — a serious agency easily
          spends several weeks on it. That's the work you're paying for upfront when you buy
          a website.
        </p>
        <p>
          The problem? For many Mauritian entrepreneurs — especially those just starting
          out — spending <strong className="text-white">Rs 25,000 to Rs 60,000 all at once</strong> is a huge obstacle. The result:
          they delay, they go without, or they opt for something cheap that brings in no
          clients at all.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          What it actually costs in 2026
        </h2>
        <p>There are two ways to get a professional website in Mauritius today:</p>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7">
          <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">
            Option 1 · One-time purchase
          </div>
          <p className="mt-3 text-white/75">
            You pay everything upfront. The website is 100% yours. Budget between{" "}
            <strong className="text-white">Rs 25,000 and Rs 80,000</strong> depending on complexity —
            simple showcase site, e-commerce, specific features. This is the traditional
            model.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7">
          <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">
            Option 2 · Monthly subscription
          </div>
          <p className="mt-3 text-white/75">
            No large upfront cost. You get a{" "}
            <strong className="text-white">professional website delivered in 15 to 30 days</strong>, hosting and domain
            name included, for a monthly fee. That's the model we developed at EM Group —
            <strong className="text-white"> starting from Rs 3,500 per month</strong> — because we saw too many Mauritian
            entrepreneurs held back by the upfront investment.
          </p>
        </div>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Which one should you choose?
        </h2>
        <p>
          If you have the budget and want to own your website from day one —{" "}
          <strong className="text-white">purchase</strong> is the right fit.
        </p>
        <p>
          If you're starting out, want to test before investing heavily, or prefer to spread
          your costs — <strong className="text-white">the subscription model</strong> makes complete sense. You get exactly
          the same professional result, without spending Rs 25,000 upfront.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          What we see on the ground in Mauritius
        </h2>
        <p>
          Most entrepreneurs we meet didn't know the subscription option existed. They
          assumed a professional website was simply out of reach.{" "}
          <strong className="text-white">Rs 3,500 per month is often less than their monthly phone bill.</strong>
        </p>
        <p>
          A website that brings in no clients, regardless of its price, is wasted money.
          What matters is that it's well built, well ranked on Google, and gives visitors a
          reason to contact you.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          In summary
        </h2>
        <p>
          A website in Mauritius costs between{" "}
          <strong className="text-white">Rs 3,500/month on subscription</strong> and{" "}
          <strong className="text-white">Rs 25,000 to Rs 80,000 for a one-time purchase</strong>. The right choice depends on
          your situation, your budget and your goals.
        </p>
        <p>
          If you'd like us to look at what best suits your project — we offer a free quote
          within 24 hours.
        </p>
      </BlogLayout>
    </>
  );
}
